import { generateId } from 'vince-core/utils/string';
import {
    type ColumnConfig,
    type ColumnsConfig,
    type ExpandedSchemaProperty,
    PropertyFormat,
    type Schema,
    type Table,
    UpdateType
} from 'vince-core/types/tables';
import { DataType } from 'vince-core/types/common';
import { JsonSchemaType } from 'vince-core/types/formats';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sortByDisplayOrder (entries: [string, any][], displayOrder: string[]) {
    return entries.sort(([a], [b]) => {
        const indexA = displayOrder.indexOf(a);
        const indexB = displayOrder.indexOf(b);
        // If not found in displayOrder sort alphabetically
        if (indexA === -1 && indexB === -1) {
            return a.localeCompare(b);
        }
        if (indexA === -1) { return 1; }
        if (indexB === -1) { return -1; }
        return indexA - indexB;
    });
}

export function createNewTable (): Table {
    return {
        columnConfig: {},
        primaryKeys: [],
        displayOrder: [],
        schema: {
            type: JsonSchemaType.OBJECT,
            properties: {},
            required: []
        },
        tableAlias: '',
        tableName: '',
        entity: 'META',
        rowCount: 0,
        updateType: UpdateType.UPSERT,
        timestamp: '',
        shardMax: 0
    };
}

export const inferSchema = (column: ColumnConfig): {type: JsonSchemaType, format: PropertyFormat | undefined} => {
    let type = JsonSchemaType.STRING;
    let format;
    if (column.dataType === DataType.NUMERIC) {
        type = JsonSchemaType.NUMBER;
    } else if (column.dataType === DataType.DATE) {
        type = JsonSchemaType.STRING;
        format = PropertyFormat.M3_DATE; // we assume m3 data since that is 99% likely
    }

    return { type, format };
};

// Convert column config to JSON schema format
export const convertColumnConfigToSchema = (columnConfig: ColumnsConfig): Schema => {
    const schema = {
        type: 'object',
        properties: {},
        required: []
    } as Schema;
    const keys = Object.keys(columnConfig || {});
    keys.forEach((key) => {
        const column = columnConfig[key]!;
        const { type, format } = inferSchema(column);
        schema.properties[key] = {
            type,
            title: column!.aliasName,
            format
        };
    });
    return schema;
};

// Conver JSON schema to old column config format for compatibility
export const convertSchemaToColumnConfig = (schema: Schema) => {
    const columnConfig = {} as ColumnsConfig;
    Object.keys(schema.properties || {}).forEach((key) => {
        const column = schema.properties[key];
        let dataType = DataType.STRING;
        if ([JsonSchemaType.NUMBER, JsonSchemaType.INTEGER].includes(column!.type)) {
            dataType = DataType.NUMERIC;
        } else if (column?.format?.includes('date')) {
            dataType = DataType.DATE;
        }
        columnConfig[key] = {
            aliasName: column!.title!,
            dataType
        };
    });
    return columnConfig;
};

export function getColumns (table: Table): ExpandedSchemaProperty[] {
    // TODO: Convert to schema if schema not present
    if (Object.keys(table.schema ?? {}).length === 0) {
        table.schema = convertColumnConfigToSchema(table.columnConfig);
    }
    return getColumnsFromSchema(table);
}

export function getColumnsFromSchema (table: Table): ExpandedSchemaProperty[] {
    const displayOrder = table.displayOrder || [];
    const properties = Object.entries(table.schema!.properties);
    const sortedProperties = sortByDisplayOrder(properties, displayOrder);

    const test = sortedProperties.map(([key, value]) => ({
        id: generateId(),
        ...value,
        name: key,
        selectedType: {
            type: value.type,
            ...(value.format ? { format: value.format } : {}),
            ...(value.items ? { items: value.items } : {})
        },
        primaryKey: table.primaryKeys.includes(key)
    }));
    return test;
}
